// LocationManager.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Grid,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Alert,
    Tab,
    Tabs,
    CircularProgress,
    Card,
    CardContent,
    Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Wifi as WifiIcon,
    LocationOn as LocationIcon,
    QrCode as QrCodeIcon,
    Download as DownloadIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc, query, orderBy, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { registerApprovedNetwork } from './WiFiVerification';
import { QRCodeCanvas } from 'qrcode.react'; // You'll need to install this: npm install qrcode.react

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`location-tabpanel-${index}`}
            aria-labelledby={`location-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const LocationManager = ({ orgId, userId, appUrl }) => {
    const [tabValue, setTabValue] = useState(0);
    const [locations, setLocations] = useState([]);
    const [networks, setNetworks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newLocation, setNewLocation] = useState({ name: '', address: '', description: '' });
    const [newNetwork, setNewNetwork] = useState({ name: '', internalResourceUrl: '' });
    const [editItem, setEditItem] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null, type: '' });
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
    const [qrCodeDialog, setQrCodeDialog] = useState({ open: false, location: null });

    // Use the current origin if appUrl is not provided
    const baseUrl = appUrl || window.location.origin;

    useEffect(() => {
        fetchLocations();
        fetchNetworks();
    }, [orgId]);

    const fetchLocations = async () => {
        setLoading(true);
        try {
            const locationsRef = collection(db, "organizations", orgId, "locations");
            const q = query(locationsRef, orderBy("name"));
            const querySnapshot = await getDocs(q);

            const locationsList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setLocations(locationsList);
        } catch (error) {
            console.error("Error fetching locations:", error);
            setNotification({
                open: true,
                message: "Error loading locations: " + error.message,
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchNetworks = async () => {
        setLoading(true);
        try {
            const networksRef = collection(db, "organizations", orgId, "approvedNetworks");
            const q = query(networksRef, orderBy("name"));
            const querySnapshot = await getDocs(q);

            const networksList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setNetworks(networksList);
        } catch (error) {
            console.error("Error fetching networks:", error);
            setNotification({
                open: true,
                message: "Error loading networks: " + error.message,
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleInputChange = (e, type) => {
        const { name, value } = e.target;

        if (type === 'location') {
            setNewLocation({ ...newLocation, [name]: value });
        } else if (type === 'network') {
            setNewNetwork({ ...newNetwork, [name]: value });
        }
    };

    const addLocation = async () => {
        if (!newLocation.name || !newLocation.address) {
            setNotification({
                open: true,
                message: "Please fill in required fields",
                severity: "warning"
            });
            return;
        }

        try {
            const locationsRef = collection(db, "organizations", orgId, "locations");

            await addDoc(locationsRef, {
                name: newLocation.name,
                address: newLocation.address,
                description: newLocation.description || "",
                createdAt: new Date(),
                createdBy: userId
            });

            // Reset form
            setNewLocation({ name: '', address: '', description: '' });

            // Reload locations
            fetchLocations();

            setNotification({
                open: true,
                message: "Location added successfully",
                severity: "success"
            });
        } catch (error) {
            console.error("Error adding location:", error);
            setNotification({
                open: true,
                message: "Error adding location: " + error.message,
                severity: "error"
            });
        }
    };

    const addNetwork = async () => {
        if (!newNetwork.name) {
            setNotification({
                open: true,
                message: "Please provide a network name",
                severity: "warning"
            });
            return;
        }

        try {
            // Register the current network with the provided name
            await registerApprovedNetwork(orgId, {
                name: newNetwork.name,
                internalResourceUrl: newNetwork.internalResourceUrl || null,
                userId: userId
            });

            // Reset form
            setNewNetwork({ name: '', internalResourceUrl: '' });

            // Reload networks
            fetchNetworks();

            setNotification({
                open: true,
                message: "Current network registered successfully",
                severity: "success"
            });
        } catch (error) {
            console.error("Error registering network:", error);
            setNotification({
                open: true,
                message: "Error registering network: " + error.message,
                severity: "error"
            });
        }
    };

    const openEditDialog = (item, type) => {
        if (type === 'location') {
            setNewLocation({
                name: item.name,
                address: item.address,
                description: item.description || ''
            });
        } else if (type === 'network') {
            setNewNetwork({
                name: item.name,
                internalResourceUrl: item.internalResourceUrl || ''
            });
        }

        setEditItem({ id: item.id, type });
    };

    const updateItem = async () => {
        if (!editItem) return;

        try {
            if (editItem.type === 'location') {
                const locationRef = doc(db, "organizations", orgId, "locations", editItem.id);
                await updateDoc(locationRef, {
                    name: newLocation.name,
                    address: newLocation.address,
                    description: newLocation.description || "",
                    updatedAt: new Date(),
                    updatedBy: userId
                });

                // Reload locations
                fetchLocations();
            } else if (editItem.type === 'network') {
                const networkRef = doc(db, "organizations", orgId, "approvedNetworks", editItem.id);
                await updateDoc(networkRef, {
                    name: newNetwork.name,
                    internalResourceUrl: newNetwork.internalResourceUrl || null,
                    updatedAt: new Date(),
                    updatedBy: userId
                });

                // Reload networks
                fetchNetworks();
            }

            // Reset form and edit state
            setNewLocation({ name: '', address: '', description: '' });
            setNewNetwork({ name: '', internalResourceUrl: '' });
            setEditItem(null);

            setNotification({
                open: true,
                message: `${editItem.type === 'location' ? 'Location' : 'Network'} updated successfully`,
                severity: "success"
            });
        } catch (error) {
            console.error(`Error updating ${editItem.type}:`, error);
            setNotification({
                open: true,
                message: `Error updating ${editItem.type}: ${error.message}`,
                severity: "error"
            });
        }
    };

    const openDeleteDialog = (item, type) => {
        setDeleteDialog({
            open: true,
            item,
            type
        });
    };

    const closeDeleteDialog = () => {
        setDeleteDialog({
            ...deleteDialog,
            open: false
        });
    };

    const deleteItem = async () => {
        try {
            if (deleteDialog.type === 'location') {
                await deleteDoc(doc(db, "organizations", orgId, "locations", deleteDialog.item.id));
                fetchLocations();
            } else if (deleteDialog.type === 'network') {
                await deleteDoc(doc(db, "organizations", orgId, "approvedNetworks", deleteDialog.item.id));
                fetchNetworks();
            }

            setNotification({
                open: true,
                message: `${deleteDialog.type === 'location' ? 'Location' : 'Network'} deleted successfully`,
                severity: "success"
            });
        } catch (error) {
            console.error(`Error deleting ${deleteDialog.type}:`, error);
            setNotification({
                open: true,
                message: `Error deleting ${deleteDialog.type}: ${error.message}`,
                severity: "error"
            });
        } finally {
            closeDeleteDialog();
        }
    };

    const cancelEdit = () => {
        setNewLocation({ name: '', address: '', description: '' });
        setNewNetwork({ name: '', internalResourceUrl: '' });
        setEditItem(null);
    };

    const closeNotification = () => {
        setNotification({ ...notification, open: false });
    };

    // QR Code related functions
    const openQRCodeDialog = (location) => {
        setQrCodeDialog({
            open: true,
            location
        });
    };

    const closeQRCodeDialog = () => {
        setQrCodeDialog({
            open: false,
            location: null
        });
    };

    const generateQRCodeData = (location) => {
        // Create a data object that identifies both organization and location
        const qrData = {
            orgId: orgId,
            locationId: location.id,
            type: 'location_verification',
            locationName: location.name
        };

        // Return JSON string
        return JSON.stringify(qrData);
    };

    const generateQRCodeUrl = (location) => {
        // Create a URL with query parameters for the location and organization
        const qrUrl = `${baseUrl}/verify?orgId=${orgId}&locationId=${location.id}&type=location`;

        return qrUrl;
    };

    const copyQRCodeData = () => {
        if (!qrCodeDialog.location) return;

        const qrData = generateQRCodeData(qrCodeDialog.location);
        navigator.clipboard.writeText(qrData);

        setNotification({
            open: true,
            message: "QR code data copied to clipboard",
            severity: "success"
        });
    };

    const copyQRCodeUrl = () => {
        if (!qrCodeDialog.location) return;

        const qrUrl = generateQRCodeUrl(qrCodeDialog.location);
        navigator.clipboard.writeText(qrUrl);

        setNotification({
            open: true,
            message: "QR code URL copied to clipboard",
            severity: "success"
        });
    };

    const downloadQRCode = () => {
        if (!qrCodeDialog.location) return;

        const canvas = document.getElementById('location-qrcode');
        if (!canvas) return;

        const url = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `${qrCodeDialog.location.name.replace(/\s+/g, '-')}-qrcode.png`;
        link.href = url;
        link.click();
    };

    return (
        <Box>
            <Paper elevation={3} sx={{ mb: 4 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="fullWidth"
                >
                    <Tab icon={<LocationIcon />} label="Locations" />
                    <Tab icon={<WifiIcon />} label="Wi-Fi Networks" />
                </Tabs>

                {/* Locations Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Typography variant="h6" gutterBottom>
                        {editItem && editItem.type === 'location' ? 'Edit Location' : 'Add New Location'}
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Location Name"
                                name="name"
                                value={newLocation.name}
                                onChange={(e) => handleInputChange(e, 'location')}
                                required
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Address"
                                name="address"
                                value={newLocation.address}
                                onChange={(e) => handleInputChange(e, 'location')}
                                required
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={newLocation.description}
                                onChange={(e) => handleInputChange(e, 'location')}
                                multiline
                                rows={2}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            {editItem && editItem.type === 'location' ? (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={updateItem}
                                        startIcon={<EditIcon />}
                                    >
                                        Update Location
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={cancelEdit}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={addLocation}
                                    startIcon={<AddIcon />}
                                >
                                    Add Location
                                </Button>
                            )}
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom>
                        Registered Locations
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : locations.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
                            No locations have been registered yet
                        </Typography>
                    ) : (
                        <List>
                            {locations.map((location) => (
                                <ListItem key={location.id} divider>
                                    <ListItemText
                                        primary={location.name}
                                        secondary={
                                            <>
                                                <Typography component="span" variant="body2">
                                                    {location.address}
                                                </Typography>
                                                {location.description && (
                                                    <Typography component="p" variant="body2" color="text.secondary">
                                                        {location.description}
                                                    </Typography>
                                                )}
                                            </>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <Tooltip title="Generate QR Code">
                                            <IconButton
                                                edge="end"
                                                aria-label="qr-code"
                                                onClick={() => openQRCodeDialog(location)}
                                                sx={{ mr: 1 }}
                                            >
                                                <QrCodeIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <IconButton
                                            edge="end"
                                            aria-label="edit"
                                            onClick={() => openEditDialog(location, 'location')}
                                            sx={{ mr: 1 }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            onClick={() => openDeleteDialog(location, 'location')}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </TabPanel>

                {/* Networks Tab */}
                <TabPanel value={tabValue} index={1}>
                    <Typography variant="h6" gutterBottom>
                        {editItem && editItem.type === 'network' ? 'Edit Wi-Fi Network' : 'Register Current Wi-Fi Network'}
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Network Name"
                                name="name"
                                value={newNetwork.name}
                                onChange={(e) => handleInputChange(e, 'network')}
                                required
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Internal Resource URL (Optional)"
                                name="internalResourceUrl"
                                value={newNetwork.internalResourceUrl}
                                onChange={(e) => handleInputChange(e, 'network')}
                                helperText="URL to an internal resource only accessible on this network"
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            {editItem && editItem.type === 'network' ? (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={updateItem}
                                        startIcon={<EditIcon />}
                                    >
                                        Update Network
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={cancelEdit}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={addNetwork}
                                    startIcon={<WifiIcon />}
                                >
                                    Register Current Network
                                </Button>
                            )}
                        </Grid>
                    </Grid>

                    <Alert severity="info" sx={{ mt: 2 }}>
                        This will register your current Wi-Fi network's characteristics for verification. Employees must be on the same network to pass verification.
                    </Alert>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom>
                        Registered Networks
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : networks.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
                            No networks have been registered yet
                        </Typography>
                    ) : (
                        <List>
                            {networks.map((network) => (
                                <ListItem key={network.id} divider>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <WifiIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                {network.name}
                                            </Box>
                                        }
                                        secondary={
                                            <>
                                                {network.createdAt && (
                                                    <Typography component="span" variant="body2">
                                                        Registered: {network.createdAt.toDate ? network.createdAt.toDate().toLocaleString() : new Date(network.createdAt).toLocaleString()}
                                                    </Typography>
                                                )}
                                                {network.internalResourceUrl && (
                                                    <Typography component="p" variant="body2" color="text.secondary">
                                                        Internal Resource: {network.internalResourceUrl}
                                                    </Typography>
                                                )}
                                            </>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            aria-label="edit"
                                            onClick={() => openEditDialog(network, 'network')}
                                            sx={{ mr: 1 }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            onClick={() => openDeleteDialog(network, 'network')}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </TabPanel>
            </Paper>

            {/* QR Code Dialog */}
            <Dialog
                open={qrCodeDialog.open}
                onClose={closeQRCodeDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Location QR Code: {qrCodeDialog.location?.name}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                        <Box sx={{ mb: 2, p: 2, border: '1px solid #ccc', borderRadius: 2, textAlign: 'center' }}>
                            {qrCodeDialog.location && (
                                <QRCodeCanvas
                                    id="location-qrcode"
                                    value={generateQRCodeData(qrCodeDialog.location)}
                                    size={256}
                                    level="H"
                                    includeMargin={true}
                                />
                            )}
                        </Box>
                        <Typography variant="subtitle1" gutterBottom>
                            {qrCodeDialog.location?.address}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            This QR code allows employees to clock in at this location.
                        </Typography>
                        <Alert severity="info" sx={{ mt: 2, mb: 2, width: '100%' }}>
                            This QR code does not expire. Print it and place it at your location for easy check-ins.
                        </Alert>
                        <Box sx={{ display: 'flex', mt: 2, gap: 2, justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                startIcon={<CopyIcon />}
                                onClick={copyQRCodeData}
                            >
                                Copy Data
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CopyIcon />}
                                onClick={copyQRCodeUrl}
                            >
                                Copy URL
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={downloadQRCode}
                                color="primary"
                            >
                                Download
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeQRCodeDialog}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={closeDeleteDialog}
            >
                <DialogTitle>
                    {`Delete ${deleteDialog.type === 'location' ? 'Location' : 'Network'}`}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete {deleteDialog.item?.name}? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog}>
                        Cancel
                    </Button>
                    <Button
                        onClick={deleteItem}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={closeNotification}
            >
                <Alert
                    onClose={closeNotification}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LocationManager;